import React, { useState, useEffect } from 'react';
import { 
  Workflow, ChevronDown, ChevronRight, Plus, Trash2, Edit2, Save, X,
  Zap, Clock, MessageSquare, Mail, Bell, Brain, Globe, Edit, 
  Play, Pause, Settings, Copy, CheckCircle, AlertTriangle, RefreshCw,
  ArrowRight, User, Users, Briefcase, Phone, GripVertical,
  LayoutList, GitBranch, ListTree, MessageCircle,
  Database, Download, Upload, ListPlus, Shield, Calendar, FileText,
  AlertCircle, ToggleRight, ToggleLeft
} from 'lucide-react';
import { toast } from 'sonner';
import WorkflowSchema from './WorkflowSchema';
import WorkflowTree from './WorkflowTree';
import WorkflowCockpit from './WorkflowCockpit';
import WorkflowAIConfigurator from './WorkflowAIConfigurator';
import WorkflowAudit from './WorkflowAudit';
import AutomationPanel from './AutomationPanel';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : PIPELINE WORKFLOW
// ════════════════════════════════════════════════════════════════════════════════

const PipelineWorkflow = () => {
  const [stages, setStages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);
  const [triggerTypes, setTriggerTypes] = useState({ types: [], sources: [] });
  const [actionTypes, setActionTypes] = useState({ types: [], targets: [] });
  const [showTemplates, setShowTemplates] = useState(false);
  const [variables, setVariables] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'schema'

  // Charger les données
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [stagesRes, templatesRes, triggerRes, actionRes, varsRes] = await Promise.all([
        authFetch(`${API}/pipeline-workflow/stages`).then(r => r.json()),
        authFetch(`${API}/pipeline-workflow/templates`).then(r => r.json()),
        authFetch(`${API}/pipeline-workflow/trigger-types`).then(r => r.json()),
        authFetch(`${API}/pipeline-workflow/action-types`).then(r => r.json()),
        authFetch(`${API}/pipeline-workflow/variables`).then(r => r.json())
      ]);
      
      setStages(stagesRes.stages || []);
      setTemplates(templatesRes.templates || []);
      setTriggerTypes(triggerRes);
      setActionTypes(actionRes);
      setVariables(varsRes.variables || []);
    } catch (error) {
      console.error('Erreur chargement workflow:', error);
      toast.error('Erreur chargement des workflows');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder le workflow d'une étape
  const saveStageWorkflow = async (stageId, workflow) => {
    try {
      const res = await authFetch(`${API}/pipeline-workflow/stages/${stageId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_id: stageId, workflow })
      });
      
      if (res.ok) {
        toast.success('Workflow sauvegardé');
        // Update local state
        setStages(prev => prev.map(s => 
          s.stage_id === stageId ? { ...s, workflow } : s
        ));
      } else {
        throw new Error('Erreur sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur sauvegarde workflow');
    }
  };

  // Toggle trigger/action enabled
  const toggleItem = (stageId, section, itemId) => {
    const stage = stages.find(s => s.stage_id === stageId);
    if (!stage) return;

    const workflow = { ...stage.workflow };
    workflow[section] = workflow[section].map(item => 
      item.id === itemId ? { ...item, enabled: !item.enabled } : item
    );
    
    saveStageWorkflow(stageId, workflow);
  };

  // Ajouter un item
  const addItem = (stageId, section, newItem) => {
    const stage = stages.find(s => s.stage_id === stageId);
    if (!stage) return;

    const workflow = { ...stage.workflow };
    workflow[section] = [...(workflow[section] || []), newItem];
    
    saveStageWorkflow(stageId, workflow);
  };

  // Supprimer un item
  const deleteItem = (stageId, section, itemId) => {
    const stage = stages.find(s => s.stage_id === stageId);
    if (!stage) return;

    const workflow = { ...stage.workflow };
    workflow[section] = workflow[section].filter(item => item.id !== itemId);
    
    saveStageWorkflow(stageId, workflow);
  };

  // Modifier un item
  const updateItem = (stageId, section, itemId, updates) => {
    const stage = stages.find(s => s.stage_id === stageId);
    if (!stage) return;

    const workflow = { ...stage.workflow };
    workflow[section] = workflow[section].map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    );
    
    saveStageWorkflow(stageId, workflow);
  };

  // Type badge colors
  const getTypeBadge = (type) => {
    const badges = {
      auto: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: <Zap size={12} />, label: 'Auto' },
      semi_auto: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: <User size={12} />, label: 'Semi-auto' },
      manual: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: <Edit size={12} />, label: 'Manuel' },
      conversational: { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: <MessageSquare size={12} />, label: 'Conv.' }
    };
    return badges[type] || badges.manual;
  };

  // Action type icon
  const getActionIcon = (type) => {
    const icons = {
      sms: <MessageSquare size={16} />,
      whatsapp: <MessageCircle size={16} className="text-green-400" />,
      email: <Mail size={16} />,
      notification: <Bell size={16} />,
      christelle_ia: <Brain size={16} />,
      webhook: <Globe size={16} />,
      update_field: <Edit size={16} />
    };
    return icons[type] || <Zap size={16} />;
  };

  // Target icon
  const getTargetIcon = (target) => {
    const icons = {
      client: <User size={14} />,
      technicien: <Briefcase size={14} />,
      telepro: <Phone size={14} />,
      manager: <Users size={14} />
    };
    return icons[target] || <User size={14} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Workflow className="w-8 h-8 text-purple-400" />
            <div>
              <h2 className="text-2xl font-bold text-white">Pipeline Workflow</h2>
              <p className="text-slate-400">Configuration des automatisations par étape</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle Vue Liste / Schéma / Arbre */}
            <div className="flex items-center bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('audit')}
                data-testid="tab-audit"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'audit'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Shield size={16} />
                Audit
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'list'
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <LayoutList size={16} />
                Liste
              </button>
              <button
                onClick={() => setViewMode('schema')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'schema'
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <GitBranch size={16} />
                Schéma
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'tree'
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ListTree size={16} />
                Arbre
              </button>
              <button
                onClick={() => setViewMode('cockpit')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'cockpit'
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Workflow size={16} />
                Cockpit
              </button>
              <button
                onClick={() => setViewMode('configurator')}
                data-testid="tab-configurator"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'configurator'
                    ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/25'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Brain size={16} />
                Configurateur IA
              </button>
            </div>
            
            {viewMode === 'list' && (
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  showTemplates 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <MessageSquare size={16} />
                Templates ({templates.length})
              </button>
            )}
            <button
              onClick={loadData}
              className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Vue Audit */}
      {viewMode === 'audit' && (
        <WorkflowAudit />
      )}

      {/* Vue Schéma */}
      {viewMode === 'schema' && (
        <WorkflowSchema />
      )}

      {/* Vue Arbre */}
      {viewMode === 'tree' && (
        <WorkflowTree />
      )}

      {/* Vue Cockpit */}
      {viewMode === 'cockpit' && (
        <WorkflowCockpit />
      )}

      {/* Vue Configurateur IA */}
      {viewMode === 'configurator' && (
        <WorkflowAIConfigurator />
      )}

      {/* RÈGLES DE SYNCHRONISATION MIGRATION */}
      {viewMode !== 'configurator' && <SyncRulesPanel />}
      {viewMode !== 'configurator' && <AutomationPanel />}

      {/* Vue Liste (contenu existant) */}
      {viewMode === 'list' && (
        <>
          {/* Légende des types */}
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="text-slate-500">Types :</span>
            {triggerTypes.types?.map(t => {
              const badge = getTypeBadge(t.id);
              return (
                <div key={t.id} className={`flex items-center gap-1 px-2 py-1 rounded ${badge.bg} ${badge.text}`} title={t.description}>
                  {badge.icon}
                  <span>{t.label}</span>
                </div>
              );
            })}
          </div>

          {/* Templates Panel (collapsible) */}
          {showTemplates && (
            <TemplatesPanel templates={templates} variables={variables} />
          )}

      {/* Liste des étapes avec workflows */}
      <div className="space-y-3">
        {stages.filter(s => s.stage_actif).map(stage => (
          <StageWorkflowCard
            key={stage.stage_id}
            stage={stage}
            stages={stages}
            expanded={expandedStage === stage.stage_id}
            onToggle={() => setExpandedStage(expandedStage === stage.stage_id ? null : stage.stage_id)}
            onToggleItem={(section, itemId) => toggleItem(stage.stage_id, section, itemId)}
            onAddItem={(section, item) => addItem(stage.stage_id, section, item)}
            onDeleteItem={(section, itemId) => deleteItem(stage.stage_id, section, itemId)}
            onUpdateItem={(section, itemId, updates) => updateItem(stage.stage_id, section, itemId, updates)}
            getTypeBadge={getTypeBadge}
            getActionIcon={getActionIcon}
            getTargetIcon={getTargetIcon}
            templates={templates}
            triggerTypes={triggerTypes}
            actionTypes={actionTypes}
          />
        ))}
      </div>

      {/* Info SMS désactivé */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-400">SMS en attente d'activation</h4>
            <p className="text-sm text-slate-400 mt-1">
              Les actions SMS sont désactivées en attendant l'approbation du numéro Twilio (1-3 jours).
              Les autres actions (notifications, Christelle IA) sont opérationnelles.
            </p>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : PANNEAU DES TEMPLATES
// ════════════════════════════════════════════════════════════════════════════════

const TemplatesPanel = ({ templates, variables }) => {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-purple-400" />
        Bibliothèque de Templates
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map(template => (
          <div 
            key={template.id}
            className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 hover:border-purple-500/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <span className={`px-2 py-0.5 rounded text-xs ${
                template.type === 'sms' ? 'bg-blue-500/20 text-blue-400' :
                template.type === 'whatsapp' ? 'bg-green-500/20 text-green-400' :
                template.type === 'email' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-amber-500/20 text-amber-400'
              }`}>
                {template.type === 'whatsapp' ? '📱 WHATSAPP' : template.type.toUpperCase()}
              </span>
              <span className="text-xs text-slate-500">{template.category}</span>
            </div>
            <h4 className="font-medium text-white text-sm mb-1">{template.name}</h4>
            <p className="text-xs text-slate-400 line-clamp-2">{template.content}</p>
            {template.variables?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {template.variables.slice(0, 3).map(v => (
                  <span key={v} className="text-xs bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">
                    {`{{${v}}}`}
                  </span>
                ))}
                {template.variables.length > 3 && (
                  <span className="text-xs text-slate-500">+{template.variables.length - 3}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : CARTE WORKFLOW D'UNE ÉTAPE
// ════════════════════════════════════════════════════════════════════════════════

const StageWorkflowCard = ({ 
  stage, stages, expanded, onToggle, onToggleItem, onAddItem, onDeleteItem, onUpdateItem,
  getTypeBadge, getActionIcon, getTargetIcon,
  templates, triggerTypes, actionTypes
}) => {
  const workflow = stage.workflow || {};
  const entryTriggers = workflow.entry_triggers || [];
  const entryActions = workflow.entry_actions || [];
  const exitTriggers = workflow.exit_triggers || [];

  const activeCount = [
    ...entryTriggers.filter(t => t.enabled),
    ...entryActions.filter(a => a.enabled),
    ...exitTriggers.filter(t => t.enabled)
  ].length;

  const inactiveCount = [
    ...entryTriggers.filter(t => !t.enabled),
    ...entryActions.filter(a => !a.enabled),
    ...exitTriggers.filter(t => !t.enabled)
  ].length;

  const totalCount = entryTriggers.length + entryActions.length + exitTriggers.length;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: stage.stage_color }}
          />
          <span className="font-medium text-white">{stage.stage_name}</span>
          <span className="text-xs text-slate-500">({stage.stage_id})</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicateurs actifs/inactifs */}
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                {activeCount} actif{activeCount > 1 ? 's' : ''}
              </span>
            )}
            {inactiveCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-600/50 text-slate-400 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                {inactiveCount} inactif{inactiveCount > 1 ? 's' : ''}
              </span>
            )}
            {totalCount === 0 && (
              <span className="text-xs text-slate-500 italic">Aucune config</span>
            )}
          </div>
          {expanded ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronRight size={20} className="text-slate-400" />}
        </div>
      </button>

      {/* Contenu expandé */}
      {expanded && (
        <div className="border-t border-slate-700 p-4 space-y-6">
          {/* Déclencheurs d'entrée */}
          <WorkflowSection
            title="🔄 Déclencheurs d'entrée"
            subtitle="Ce qui fait arriver un lead dans cette étape"
            items={entryTriggers}
            section="entry_triggers"
            type="trigger"
            onToggle={(itemId) => onToggleItem('entry_triggers', itemId)}
            onAdd={(item) => onAddItem('entry_triggers', item)}
            onDelete={(itemId) => onDeleteItem('entry_triggers', itemId)}
            onUpdate={(itemId, updates) => onUpdateItem('entry_triggers', itemId, updates)}
            getTypeBadge={getTypeBadge}
            triggerTypes={triggerTypes}
            stages={stages}
            currentStageId={stage.stage_id}
          />

          {/* Actions à l'entrée */}
          <WorkflowSection
            title="⚡ Actions à l'entrée"
            subtitle="Ce qui se passe quand un lead arrive"
            items={entryActions}
            section="entry_actions"
            type="action"
            onToggle={(itemId) => onToggleItem('entry_actions', itemId)}
            onAdd={(item) => onAddItem('entry_actions', item)}
            onDelete={(itemId) => onDeleteItem('entry_actions', itemId)}
            onUpdate={(itemId, updates) => onUpdateItem('entry_actions', itemId, updates)}
            getActionIcon={getActionIcon}
            getTargetIcon={getTargetIcon}
            templates={templates}
            actionTypes={actionTypes}
          />

          {/* Déclencheurs de sortie */}
          <WorkflowSection
            title="🚀 Déclencheurs de sortie"
            subtitle="Ce qui fait passer à l'étape suivante"
            items={exitTriggers}
            section="exit_triggers"
            type="exit_trigger"
            onToggle={(itemId) => onToggleItem('exit_triggers', itemId)}
            onAdd={(item) => onAddItem('exit_triggers', item)}
            onDelete={(itemId) => onDeleteItem('exit_triggers', itemId)}
            onUpdate={(itemId, updates) => onUpdateItem('exit_triggers', itemId, updates)}
            getTypeBadge={getTypeBadge}
            triggerTypes={triggerTypes}
            stages={stages}
            currentStageId={stage.stage_id}
          />
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : SECTION WORKFLOW (TRIGGERS OU ACTIONS)
// ════════════════════════════════════════════════════════════════════════════════

const WorkflowSection = ({ 
  title, subtitle, items, section, type, onToggle, onAdd, onDelete, onUpdate,
  getTypeBadge, getActionIcon, getTargetIcon, templates, triggerTypes, actionTypes, stages, currentStageId
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleAdd = (newItem) => {
    onAdd(newItem);
    setShowAddForm(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-medium text-white">{title}</h4>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showAddForm 
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
              : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
          }`}
        >
          {showAddForm ? <X size={14} /> : <Plus size={14} />}
          {showAddForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {/* Formulaire d'ajout */}
      {showAddForm && (
        <div className="mb-4">
          {type === 'action' ? (
            <AddActionForm 
              onAdd={handleAdd} 
              onCancel={() => setShowAddForm(false)}
              templates={templates}
              actionTypes={actionTypes}
            />
          ) : (
            <AddTriggerForm 
              onAdd={handleAdd} 
              onCancel={() => setShowAddForm(false)}
              triggerTypes={triggerTypes}
              stages={stages}
              currentStageId={currentStageId}
              isExitTrigger={type === 'exit_trigger'}
            />
          )}
        </div>
      )}
      
      {/* Liste des items */}
      {(!items || items.length === 0) && !showAddForm ? (
        <p className="text-xs text-slate-500 italic py-2">Aucune configuration</p>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <WorkflowItem
              key={item.id}
              item={item}
              type={type}
              isEditing={editingId === item.id}
              onToggle={() => onToggle(item.id)}
              onEdit={() => setEditingId(editingId === item.id ? null : item.id)}
              onDelete={() => {
                if (window.confirm('Supprimer cet élément ?')) {
                  onDelete(item.id);
                }
              }}
              onUpdate={(updates) => {
                onUpdate(item.id, updates);
                setEditingId(null);
              }}
              getTypeBadge={getTypeBadge}
              getActionIcon={getActionIcon}
              getTargetIcon={getTargetIcon}
              templates={templates}
              triggerTypes={triggerTypes}
              actionTypes={actionTypes}
              stages={stages}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : ITEM DE WORKFLOW (TRIGGER OU ACTION)
// ════════════════════════════════════════════════════════════════════════════════

const WorkflowItem = ({ 
  item, type, isEditing, onToggle, onEdit, onDelete, onUpdate,
  getTypeBadge, getActionIcon, getTargetIcon, templates, triggerTypes, actionTypes, stages
}) => {
  const [editData, setEditData] = useState(item);

  useEffect(() => {
    setEditData(item);
  }, [item, isEditing]);

  if (isEditing) {
    return (
      <div className="bg-slate-900 border border-purple-500/50 rounded-lg p-4">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Description</label>
            <input
              type="text"
              value={editData.description || editData.message || ''}
              onChange={(e) => setEditData({ ...editData, description: e.target.value, message: e.target.value })}
              className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
              placeholder="Description de l'élément"
            />
          </div>

          {type === 'action' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Type d'action</label>
                  <select
                    value={editData.type || 'notification'}
                    onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    {actionTypes?.types?.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Destinataire</label>
                  <select
                    value={editData.target || 'telepro'}
                    onChange={(e) => setEditData({ ...editData, target: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    {actionTypes?.targets?.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Template</label>
                  <select
                    value={editData.template_id || ''}
                    onChange={(e) => setEditData({ ...editData, template_id: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="">-- Aucun --</option>
                    {templates?.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Délai (minutes)</label>
                  <input
                    type="number"
                    value={editData.delay_minutes || 0}
                    onChange={(e) => setEditData({ ...editData, delay_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                    min="0"
                  />
                </div>
              </div>
            </>
          )}

          {(type === 'trigger' || type === 'exit_trigger') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Type</label>
                  <select
                    value={editData.type || 'semi_auto'}
                    onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    {triggerTypes?.types?.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Source</label>
                  <select
                    value={editData.source || 'form_submit'}
                    onChange={(e) => setEditData({ ...editData, source: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    {triggerTypes?.sources?.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {type === 'exit_trigger' && (
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Étape cible</label>
                  <select
                    value={editData.target_stage || ''}
                    onChange={(e) => setEditData({ ...editData, target_stage: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="">-- Sélectionner --</option>
                    {stages?.filter(s => s.stage_actif).map(s => (
                      <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400 block mb-1">Condition (optionnel)</label>
                <input
                  type="text"
                  value={editData.condition || ''}
                  onChange={(e) => setEditData({ ...editData, condition: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white font-mono"
                  placeholder="Ex: nrp_count >= 5"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-sm text-slate-400 hover:text-white"
          >
            Annuler
          </button>
          <button
            onClick={() => onUpdate(editData)}
            className="px-3 py-1.5 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            <Save size={14} className="inline mr-1" />
            Sauvegarder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg border transition-colors group ${
        item.enabled 
          ? 'bg-slate-900/50 border-slate-600 hover:border-slate-500' 
          : 'bg-slate-900/20 border-slate-800 border-dashed'
      }`}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Indicateur de statut actif/inactif */}
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          item.enabled 
            ? 'bg-emerald-400 shadow-sm shadow-emerald-400/50' 
            : 'bg-slate-600'
        }`} title={item.enabled ? 'Actif' : 'Inactif'} />
        
        {/* Type badge for triggers */}
        {(type === 'trigger' || type === 'exit_trigger') && getTypeBadge && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
            item.enabled 
              ? `${getTypeBadge(item.type).bg} ${getTypeBadge(item.type).text}` 
              : 'bg-slate-700/50 text-slate-500'
          }`}>
            {getTypeBadge(item.type).icon}
          </div>
        )}
        
        {/* Action icon */}
        {type === 'action' && getActionIcon && (
          <div className={`p-2 rounded-lg ${
            !item.enabled ? 'bg-slate-800/50 text-slate-500' :
            item.type === 'sms' ? 'bg-blue-500/20 text-blue-400' :
            item.type === 'whatsapp' ? 'bg-green-500/20 text-green-400' :
            item.type === 'email' ? 'bg-emerald-500/20 text-emerald-400' :
            item.type === 'notification' ? 'bg-amber-500/20 text-amber-400' :
            item.type === 'christelle_ia' ? 'bg-purple-500/20 text-purple-400' :
            'bg-slate-700 text-slate-400'
          }`}>
            {getActionIcon(item.type)}
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${item.enabled ? 'text-white' : 'text-slate-500'}`}>
              {item.description || item.message || `Action ${item.type}`}
            </span>
            {!item.enabled && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-500">
                Désactivé
              </span>
            )}
            {type === 'exit_trigger' && item.target_stage && (
              <span className={`flex items-center gap-1 text-xs ${item.enabled ? 'text-purple-400' : 'text-slate-500'}`}>
                <ArrowRight size={12} />
                {item.target_stage}
              </span>
            )}
          </div>
          
          {/* Action details */}
          {type === 'action' && (
            <div className="flex items-center gap-2 mt-1">
              {item.target && getTargetIcon && (
                <span className={`flex items-center gap-1 text-xs ${item.enabled ? 'text-slate-400' : 'text-slate-600'}`}>
                  {getTargetIcon(item.target)}
                  {item.target}
                </span>
              )}
              {item.template_id && (
                <span className={`text-xs ${item.enabled ? 'text-slate-400' : 'text-slate-600'}`}>
                  Template: {item.template_id}
                </span>
              )}
              {item.delay_minutes > 0 && (
                <span className={`flex items-center gap-1 text-xs ${item.enabled ? 'text-slate-400' : 'text-slate-600'}`}>
                  <Clock size={10} />
                  +{item.delay_minutes}min
                </span>
              )}
            </div>
          )}
          
          {/* Trigger condition */}
          {(type === 'trigger' || type === 'exit_trigger') && item.condition && (
            <span className={`text-xs font-mono mt-1 block ${item.enabled ? 'text-slate-400' : 'text-slate-600'}`}>
              {item.condition}
            </span>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title="Modifier"
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Supprimer"
        >
          <Trash2 size={14} />
        </button>
        {/* Toggle switch */}
        <button
          onClick={onToggle}
          className={`relative w-10 h-5 rounded-full transition-colors ${
            item.enabled ? 'bg-emerald-500' : 'bg-slate-700'
          }`}
        >
          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            item.enabled ? 'left-5' : 'left-0.5'
          }`} />
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// FORMULAIRE : AJOUTER UN TRIGGER
// ════════════════════════════════════════════════════════════════════════════════

const AddTriggerForm = ({ onAdd, onCancel, triggerTypes, stages, currentStageId, isExitTrigger }) => {
  const [formData, setFormData] = useState({
    id: `t_${Date.now()}`,
    type: 'semi_auto',
    source: 'form_submit',
    description: '',
    condition: '',
    target_stage: '',
    enabled: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.description) {
      toast.error('Description requise');
      return;
    }
    if (isExitTrigger && !formData.target_stage) {
      toast.error('Étape cible requise pour un déclencheur de sortie');
      return;
    }
    onAdd(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-900 border border-purple-500/30 rounded-lg p-4 space-y-3">
      <div>
        <label className="text-xs text-slate-400 block mb-1">Description *</label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          placeholder="Ex: Télépro clique sur 'Pas intéressé'"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          >
            {triggerTypes?.types?.map(t => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Source</label>
          <select
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          >
            {triggerTypes?.sources?.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isExitTrigger && (
        <div>
          <label className="text-xs text-slate-400 block mb-1">Étape cible *</label>
          <select
            value={formData.target_stage}
            onChange={(e) => setFormData({ ...formData, target_stage: e.target.value })}
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
            required
          >
            <option value="">-- Sélectionner l'étape suivante --</option>
            {stages?.filter(s => s.stage_actif && s.stage_id !== currentStageId).map(s => (
              <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="text-xs text-slate-400 block mb-1">Condition (optionnel)</label>
        <input
          type="text"
          value={formData.condition}
          onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white font-mono"
          placeholder="Ex: resultat === 'pas_interesse' ou nrp_count >= 5"
        />
        <p className="text-xs text-slate-500 mt-1">Expression JavaScript pour conditionner le déclenchement</p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-400 hover:text-white"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2"
        >
          <Plus size={14} />
          Ajouter
        </button>
      </div>
    </form>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// FORMULAIRE : AJOUTER UNE ACTION
// ════════════════════════════════════════════════════════════════════════════════

const AddActionForm = ({ onAdd, onCancel, templates, actionTypes }) => {
  const [formData, setFormData] = useState({
    id: `a_${Date.now()}`,
    type: 'notification',
    target: 'telepro',
    template_id: '',
    message: '',
    delay_minutes: 0,
    enabled: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.message && !formData.template_id) {
      toast.error('Message ou template requis');
      return;
    }
    onAdd(formData);
  };

  // Filtrer les templates par type d'action
  const filteredTemplates = templates?.filter(t => {
    if (formData.type === 'sms') return t.type === 'sms';
    if (formData.type === 'whatsapp') return t.type === 'whatsapp';
    if (formData.type === 'email') return t.type === 'email';
    if (formData.type === 'notification') return t.type === 'notification';
    return true;
  }) || [];

  return (
    <form onSubmit={handleSubmit} className="bg-slate-900 border border-purple-500/30 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Type d'action</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value, template_id: '' })}
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          >
            {actionTypes?.types?.map(t => (
              <option key={t.id} value={t.id} disabled={!t.enabled}>
                {t.label} {!t.enabled ? '(indisponible)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Destinataire</label>
          <select
            value={formData.target}
            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          >
            {actionTypes?.targets?.map(t => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-1">Template</label>
        <select
          value={formData.template_id}
          onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
        >
          <option value="">-- Ou saisissez un message personnalisé --</option>
          {filteredTemplates.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-1">Message personnalisé</label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          placeholder="Ex: RDV confirmé pour {{client_nom}} le {{date_rdv}}"
          rows={2}
        />
        <p className="text-xs text-slate-500 mt-1">Variables disponibles : {'{{client_nom}}, {{date_rdv}}, {{telepro_nom}}'}, etc.</p>
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-1">Délai (minutes)</label>
        <input
          type="number"
          value={formData.delay_minutes}
          onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })}
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white"
          min="0"
          placeholder="0 = immédiat"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-400 hover:text-white"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2"
        >
          <Plus size={14} />
          Ajouter
        </button>
      </div>
    </form>
  );
};

// ════════════════════════════════════════════════════════════════════
// SYNC RULES PANEL - Règles de synchronisation MongoDB ↔ Google Sheets
// ════════════════════════════════════════════════════════════════════

const SyncRulesPanel = () => {
  const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [showLogs, setShowLogs] = useState(false);
  const [executing, setExecuting] = useState(null);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const res = await authFetch(`${API}/api/pipeline-workflow/sync-rules`);
      const data = await res.json();
      if (data.success) setRules(data.rules || []);
    } catch (e) { console.error('Erreur chargement sync rules:', e); }
  };

  const loadLogs = async () => {
    try {
      const res = await authFetch(`${API}/api/pipeline-workflow/sync-rules/logs?limit=20`);
      const data = await res.json();
      if (data.success) setLogs(data.logs || []);
    } catch (e) { console.error('Erreur chargement logs:', e); }
  };

  const toggleRule = async (ruleId, enabled) => {
    try {
      await authFetch(`${API}/api/pipeline-workflow/sync-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !enabled })
      });
      setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled: !enabled } : r));
      toast.success(enabled ? 'Règle désactivée' : 'Règle activée');
    } catch (e) { toast.error('Erreur modification règle'); }
  };

  const executeRule = async (ruleId) => {
    setExecuting(ruleId);
    try {
      const res = await authFetch(`${API}/api/pipeline-workflow/sync-rules/${ruleId}/execute`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Exécution terminée: ${data.result?.message || 'OK'}`);
        loadRules();
        if (showLogs) loadLogs();
      } else {
        toast.error(data.result?.message || 'Erreur exécution');
      }
    } catch (e) { toast.error('Erreur exécution'); }
    finally { setExecuting(null); }
  };

  const getCategoryStyle = (cat) => ({
    sync: { bg: 'bg-cyan-500/15', border: 'border-cyan-500/40', text: 'text-cyan-400', badge: 'Sync' },
    routing: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-400', badge: 'Routage' },
    policy: { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-400', badge: 'Politique' }
  }[cat] || { bg: 'bg-slate-500/15', border: 'border-slate-500/40', text: 'text-slate-400', badge: cat });

  const getTriggerIcon = (source) => ({
    cron_15min: <RefreshCw size={14} className="text-cyan-400" />,
    client_modified: <Edit size={14} className="text-blue-400" />,
    rdv_r1_pris: <Calendar size={14} className="text-green-400" />,
    api: <Globe size={14} className="text-emerald-400" />,
    system: <Shield size={14} className="text-amber-400" />
  }[source] || <Zap size={14} />);

  const getActionIcon = (type) => ({
    sync_from_sheet: <Download size={14} className="text-cyan-400" />,
    push_to_sheet: <Upload size={14} className="text-blue-400" />,
    insert_sheet_row: <ListPlus size={14} className="text-green-400" />,
    notification: <Bell size={14} className="text-emerald-400" />,
    update_field: <Shield size={14} className="text-amber-400" />
  }[type] || <Zap size={14} />);

  const sortedRules = [...rules].sort((a, b) => (a.rule_number || 0) - (b.rule_number || 0));

  return (
    <div data-testid="sync-rules-panel" className="bg-gradient-to-r from-cyan-600/10 to-blue-600/10 border border-cyan-500/30 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Database size={22} className="text-cyan-400" />
          <div className="text-left">
            <h3 className="text-lg font-bold text-white">Regles de Synchronisation</h3>
            <p className="text-xs text-slate-400">MongoDB ↔ Google Sheets — {rules.filter(r => r.enabled).length}/{rules.length} actives</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${rules.every(r => r.enabled) ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
            {rules.every(r => r.enabled) ? 'Toutes actives' : 'Partiellement actif'}
          </span>
          {expanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {sortedRules.map(rule => {
            const catStyle = getCategoryStyle(rule.category);
            return (
              <div key={rule.id} data-testid={`sync-rule-${rule.id}`}
                className={`${catStyle.bg} border ${catStyle.border} rounded-lg p-3 transition-all ${!rule.enabled ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${catStyle.text} bg-slate-900/50`}>R{rule.rule_number}</span>
                      <h4 className="text-sm font-semibold text-white truncate">{rule.name}</h4>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${catStyle.bg} ${catStyle.text}`}>{catStyle.badge}</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-2">{rule.description}</p>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className="flex items-center gap-1 text-slate-500">
                        {getTriggerIcon(rule.trigger?.source)}
                        {rule.trigger?.source === 'cron_15min' ? 'Toutes les 15min' :
                         rule.trigger?.source === 'client_modified' ? 'A chaque modification' :
                         rule.trigger?.source === 'rdv_r1_pris' ? 'Quand R1 pris' :
                         rule.trigger?.source === 'api' ? 'Via API' : 'Systeme'}
                      </span>
                      <span className="text-slate-600">→</span>
                      <span className="flex items-center gap-1 text-slate-500">
                        {getActionIcon(rule.action?.type)}
                        {rule.action?.type === 'sync_from_sheet' ? 'Sheet → Mongo' :
                         rule.action?.type === 'push_to_sheet' ? 'Mongo → Sheet' :
                         rule.action?.type === 'insert_sheet_row' ? 'Inserer ligne Sheet' :
                         rule.action?.type === 'notification' ? 'Notification' : 'Politique'}
                      </span>
                      {rule.last_run && (
                        <span className="text-slate-600 text-[10px]">
                          Dernier run: {new Date(rule.last_run).toLocaleString('fr-FR')}
                        </span>
                      )}
                      {rule.run_count > 0 && (
                        <span className="text-slate-600 text-[10px]">{rule.run_count}x</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {rule.action?.type === 'sync_from_sheet' && (
                      <button
                        onClick={() => executeRule(rule.id)}
                        disabled={executing === rule.id}
                        className="p-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 rounded text-cyan-400 text-xs transition-colors disabled:opacity-50"
                        title="Executer maintenant"
                      >
                        {executing === rule.id ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                      </button>
                    )}
                    <button
                      onClick={() => toggleRule(rule.id, rule.enabled)}
                      className={`p-1.5 rounded text-xs transition-colors ${
                        rule.enabled ? 'bg-emerald-500/20 text-emerald-400 hover:bg-red-500/20 hover:text-red-400' : 'bg-slate-700 text-slate-500 hover:bg-emerald-500/20 hover:text-emerald-400'
                      }`}
                      title={rule.enabled ? 'Desactiver' : 'Activer'}
                    >
                      {rule.enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Bouton logs */}
          <button
            onClick={() => { setShowLogs(!showLogs); if (!showLogs) loadLogs(); }}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <FileText size={14} />
            {showLogs ? 'Masquer les logs' : `Voir les logs d'execution`}
          </button>

          {showLogs && (
            <div className="bg-slate-900/50 rounded-lg p-3 max-h-48 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Aucune execution enregistree</p>
              ) : logs.map((log, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-800/50 last:border-0 text-[11px]">
                  <span className={log.result?.status === 'success' ? 'text-emerald-400' : log.result?.status === 'error' ? 'text-red-400' : 'text-slate-400'}>
                    {log.result?.status === 'success' ? <CheckCircle size={12} /> : log.result?.status === 'error' ? <AlertCircle size={12} /> : <Clock size={12} />}
                  </span>
                  <span className="text-slate-400 shrink-0">{new Date(log.executed_at).toLocaleString('fr-FR')}</span>
                  <span className="text-white font-medium truncate">{log.rule_name}</span>
                  <span className="text-slate-500 truncate">{log.result?.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelineWorkflow;
